import React, { useState } from "react";
import { Modal, Row, Col, Button } from "antd";
import { Helmet } from 'react-helmet-async';
import DocTag from "./DocTag";
import { buildDocGroups } from "./GradeHelpers";
import GradeDocumentAttacher from "./GradeDocumentAttacher";
import ConsultationModal from "./ConsultationModal";
import { getPinyin } from "../../utils/tools";
import "./DataBank.css";

const GradeDetailModal = ({
    open,
    onCancel,
    record,
    lang,
    onPreview,
    userCompanies = []
}) => {
    const [attacherVisible, setAttacherVisible] = useState(false);
    const [consultationVisible, setConsultationVisible] = useState(false);

    const canAttach = record && userCompanies && userCompanies.some(c => c.id === record.company_id);

    const title = record
        ? `${record.product_alias || ""} / ${record.brand_alias || ""} / ${record.name || ""}`
        : lang === 0
            ? "详情"
            : "Detail";

    const metaDescription = record 
        ? lang === 0
            ? `下载 ${record.company_alias || ""} ${record.brand_alias || ""} ${record.name || ""} ${record.product_alias || ""} 的技术资料：物性表 (TDS), 检测报告 (COA), 安全数据表 (SDS/MSDS), 以及查看产品照片与包装详情。`
            : `Download technical documents for ${getPinyin(record.company_alias, 2) || ""} ${getPinyin(record.brand_alias, 2) || ""} ${getPinyin(record.name, 2) || ""} ${getPinyin(record.product_alias, 2) || ""}, including Technical Data Sheet (TDS), Certificate of Analysis (COA), Safety Data Sheet (SDS/MSDS), and product photos.`
        : "Product Detail";

    return (
        <>
            {open && record && (
                <Helmet>
                    <title>{`${title} - 上海林炎工贸有限公司`}</title>
                    <meta name="description" content={metaDescription} />
                </Helmet>
            )}
            <Modal
                open={open}
                onCancel={onCancel}
                onOk={onCancel}
                width={800}
                title={title}
                footer={[
                    <Button 
                        key="consult" 
                        type="default" 
                        onClick={() => setConsultationVisible(true)}
                        style={{ float: 'left' }}
                    >
                        {lang === 0 ? "咨询 / 询价" : "Consult / Inquiry"}
                    </Button>,
                    canAttach && (
                        <Button
                            key="attach"
                            type="primary"
                            onClick={() => setAttacherVisible(true)}
                        >
                            {lang === 0 ? "添附本公司文档" : "Attach Company Document"}
                        </Button>
                    ),
                    <Button key="close" onClick={onCancel}>
                        {lang === 0 ? "关闭" : "Close"}
                    </Button>
                ]}
            >
                {record && (
                    <div className="databank-detail">
                        <Row gutter={16}>
                            <Col span={12}>
                                {(() => {
                                    const logoUrl = record.company_logo_url;
                                    const companyName = record.company_alias || "-";
                                    const brandName = record.brand_alias || "-";
                                    const companyBrandText =
                                        companyName && brandName
                                            ? `${companyName} | ${brandName}`
                                            : companyName || brandName || "-";
                                    return (
                                        <>
                                            <div className="databank-detail-field">
                                                <span className="databank-detail-label">
                                                    {lang === 0 ? "公司" : "Company"}
                                                </span>
                                                <span className="databank-detail-value">
                                                    {logoUrl && (
                                                        <img
                                                            src={logoUrl}
                                                            alt={companyName}
                                                            className="databank-detail-logo-image"
                                                        />
                                                    )}
                                                </span>
                                            </div>
                                            <div className="databank-detail-field">
                                                <span className="databank-detail-label">
                                                    {lang === 0 ? "品牌" : "Brand"}
                                                </span>
                                                <span className="databank-detail-value">
                                                    <span className="databank-detail-company-brand-text">
                                                        {companyBrandText}
                                                    </span>
                                                </span>
                                            </div>
                                        </>
                                    );
                                })()}
                            </Col>
                            <Col span={12}>
                                <div className="databank-detail-field">
                                    <span className="databank-detail-label">
                                        {lang === 0 ? "牌号" : "Grade"}
                                    </span>
                                    <span className="databank-detail-value">
                                        {record.name || "-"}
                                    </span>
                                </div>
                                <div className="databank-detail-field">
                                    <span className="databank-detail-label">
                                        {lang === 0 ? "产品" : "Product"}
                                    </span>
                                    <span className="databank-detail-value">
                                        {record.product_alias || "-"}
                                    </span>
                                </div>
                            </Col>
                        </Row>
                        <div className="databank-detail-features" style={{ marginTop: '20px' }}>
                            <div className="databank-detail-features-header">
                                <span className="databank-detail-features-title">
                                    {lang === 0 ? "牌号特性" : "Grade Features"}
                                </span>
                            </div>
                            <div className="databank-detail-features-table-wrapper" style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }}>
                                <table className="databank-detail-features-table">
                                    <thead>
                                        <tr>
                                            <th>{lang === 0 ? "特性名称" : "Feature Name"}</th>
                                            <th>{lang === 0 ? "参数" : "Parameter"}</th>
                                            <th>{lang === 0 ? "标准" : "Standard"}</th>
                                            <th>{lang === 0 ? "备注" : "Remark"}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(record.features) ? record.features.map((feature, index) => (
                                            <tr key={index}>
                                                <td>{lang === 0 ? feature.nameCn : feature.nameEn}</td>
                                                <td>{feature.parameter || "-"} {feature.unit || ''}</td>
                                                <td>{feature.standard || "-"}</td>
                                                <td>{feature.remark || "-"}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={4} style={{ textAlign: 'center' }}>{lang === 0 ? "无特性信息" : "No feature information"}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="databank-detail-documents" style={{ marginTop: '20px' }}>
                            <div className="databank-detail-documents-header">
                                <span className="databank-detail-documents-title">
                                    {lang === 0 ? "全部档案" : "Documents"}
                                    {`(${Array.isArray(record.documents) ? record.documents.length : 0}):`}
                                </span>
                            </div>
                            {(() => {
                                const { regularDocs, otherDocs } = buildDocGroups(record.documents);
                                return (
                                    <div className="databank-detail-documents-list-wrapper">
                                        <div className="databank-detail-documents-group">
                                            <span className="databank-detail-documents-group-label">
                                                {lang === 0 ? "常规档案" : "Regular"}
                                            </span>
                                            <div className="databank-detail-documents-list">
                                                {regularDocs.map((d) => (
                                                    <DocTag key={d.key} doc={d} onPreview={onPreview} gradeName={record.name} />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="databank-detail-documents-group">
                                            <span className="databank-detail-documents-group-label">
                                                {lang === 0 ? "其他档案" : "Others"}
                                            </span>
                                            <div className="databank-detail-documents-list">
                                                {otherDocs.map((d) => {
                                                    const href = d.uri;
                                                    if (!href) {
                                                        return null;
                                                    }
                                                    return <DocTag key={d.key} doc={d} onPreview={onPreview} gradeName={record.name} />;
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </Modal>
            {record && (
                <GradeDocumentAttacher
                    visible={attacherVisible}
                    onCancel={() => setAttacherVisible(false)}
                    gradeId={record.id}
                    gradeName={record.name}
                    onSuccess={() => {
                        // Optionally refresh data here if needed, but the main list might need to refresh
                        // Since this is an approval request, data won't change immediately
                    }}
                />
            )}
            {record && (
                <ConsultationModal
                    visible={consultationVisible}
                    onCancel={() => setConsultationVisible(false)}
                    record={record}
                    lang={lang}
                />
            )}
        </>
    );
};

export default GradeDetailModal;
